import { TriangleAlert } from 'lucide-react';
import { type FC, type ReactNode } from 'react';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

interface LabelledFieldProps {
  children: ReactNode;
  /**
   * Sits between the label and the control. Pair it with `descriptionId` and
   * point the control's `aria-describedby` at that id, so the sentence is
   * announced with the field rather than read as loose page text.
   */
  description?: string;
  descriptionId?: string;
  /**
   * The message under the control, or undefined while the field is valid. It
   * announces itself, so it arrives as the answer to a submit rather than
   * waiting for the user to walk back up the form.
   */
  error?: string;
  label: string;
}

/**
 * Label over control, with an optional sentence between them and an optional
 * message below. Figma 11914:189978 for the plain pair and 11914:189986 for the
 * pair with a description.
 *
 * The label is `aria-hidden` visible copy, not a `<label>`. Every control under
 * it — the design system's `Input` and its `Select` — renders its own label and
 * clips it to a 2px box, so the announced name comes from there and this is
 * what the eye reads. One name announced, one label seen, which is the call
 * `invite-users-panel.tsx` records for the same pair of components.
 *
 * The frames draw no error state for this form, so the message below borrows
 * the treatment 11949:196271 gives the invite panel's: `Icon / triangle-alert`
 * at 16 beside the text, both in text-negative, at the group's own 8px gap.
 */
export const LabelledField: FC<LabelledFieldProps> = ({
  children,
  description,
  descriptionId,
  error,
  label,
}) => (
  <div className="flex w-full flex-col gap-2">
    <div className="flex flex-col gap-1">
      <span
        aria-hidden="true"
        className="text-sm font-medium leading-5 text-[var(--ds-color-text-primary)]"
      >
        {label}
      </span>
      {/* text-secondary here is `raw-stone-600`, not the token of that name.
          The frame's text-secondary is #57534E and the exported
          --ds-color-text-secondary is #78716C, which is what Figma calls
          "muted foreground" — the drift components/empty-state.tsx records. */}
      {description !== undefined && (
        <p
          className="text-[13px] leading-5 text-[var(--ds-color-raw-stone-600)]"
          id={descriptionId}
        >
          {description}
        </p>
      )}
    </div>

    {children}

    {error !== undefined && (
      <p
        className="flex items-center gap-2 text-sm leading-5 text-[var(--ds-color-text-negative)]"
        role="alert"
      >
        <TriangleAlert
          aria-hidden="true"
          className="size-4 shrink-0"
          strokeWidth={ICON_STROKE_WIDTH}
        />
        {error}
      </p>
    )}
  </div>
);
